import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import { LeadsHeader } from "shared/app/LeadsTableView/components/LeadsHeader";
import { LeadsTable } from "shared/app/LeadsTableView/slices/LeadsTable";
import { NoLeadsSlice } from "shared/app/LeadsTableView/slices/NoLeadsSlice";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";
import { fetchStages, stagesQueryKey } from "views/settings-crm/api";
import { StageKind } from "views/settings-crm/types/stage";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

export const LeadsTableView = ({
  hasHeaderTitle = false,
  property_ids,
  unit_ids
}: {
  hasHeaderTitle?: boolean;
  property_ids?: string[];
  unit_ids?: string[];
}) => {
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      search: "",
      page: "1",
      per_page: DEFAULT_PER_PAGE
    },
    ["search", "page", "per_page"]
  );

  const [debouncedSearch] = useDebouncedValue(searchParams.search, 500);

  const {
    data: queryLeadsData,
    refetch: refetchLeads,
    isFetching
  } = useQuery({
    queryKey: ["LEADS_TABLE", searchParams.page, debouncedSearch],
    queryFn: async () => {
      return await instance
        .get("/portal/v1/leads", {
          params: {
            page: searchParams.page,
            per_page: 10,
            property_ids,
            unit_ids,
            search: debouncedSearch.trim() || undefined
          }
        })
        .then((res) => res.data as PaginatedResponse<Lead>);
    }
  });

  const { data: queryStagesData } = useQuery({
    queryKey: stagesQueryKey(StageKind.Lead),
    queryFn: () => fetchStages(StageKind.Lead)
  });

  if (queryLeadsData?.items.length === 0 && !debouncedSearch && !isFetching)
    return (
      <NoLeadsSlice
        hasHeaderTitle={hasHeaderTitle}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
      />
    );

  return (
    <>
      <LeadsHeader
        search={searchParams.search}
        hasHeaderTitle={hasHeaderTitle}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        setSearch={(search) =>
          setSearchParams({ ...searchParams, search: search })
        }
      />
      <LeadsTable
        stagesData={queryStagesData || []}
        isLoading={isFetching}
        data={queryLeadsData?.items || []}
        perPageRows={Number(DEFAULT_PER_PAGE)}
        viewPortHeightOffset={120}
        pagination={
          <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
            <Pagination
              className="md:justify-end justify-center"
              total={queryLeadsData?.page_count || 0}
              current={Number(searchParams.page)}
              onChange={(p) =>
                setSearchParams({ ...searchParams, page: String(p) })
              }
            />
          </div>
        }
      />
    </>
  );
};
